import React, { PureComponent } from 'react';
import { shape, string, bool, node, number } from 'prop-types';
import classNames from 'classnames';
import { withStyles } from 'material-ui/styles';
import {
  mainText,
  secondaryText,
  accentText,
  labelText,
  buttonText,
  error as errorColor,
} from '../styles/colors';
import {
  xxxxl as xxxxlStyles,
  xxxl as xxxlStyles,
  xxl as xxlStyles,
  xl as xlStyles,
  l as lStyles,
  m as mStyles,
  s as sStyles,
  def as defStyles,
  xs as xsStyles,
  xxs as xxsStyles,
} from '../styles/fontStyles';
import { reduceClasses } from '../utils/testUtils';

export const style = theme => ({
  root: {
    color: mainText,
    fontFamily: 'Nunito, Arial, sans-serif, dinpro-regular',
    fontSize: defStyles.sizeMobile,
    lineHeight: defStyles.lineHeightMobile,
    letterSpacing: defStyles.spacing,
    fontWeight: 400,
    [theme.breakpoints.up('sm')]: {
      fontSize: defStyles.size,
      lineHeight: defStyles.lineHeight,
    },
  },
  clickable: {
    cursor: 'pointer',
  },
  black: {
    fontFamily: 'Nunito, Arial, sans-serif, dinpro-black',
    fontWeight: 700,
  },
  bold: {
    fontFamily: 'Nunito, Arial, sans-serif, dinpro-bold',
    fontWeight: 700,
  },
  medium: {
    fontFamily: 'Nunito, Arial, sans-serif, dinpro-medium',
    fontWeight: 700,
  },
  light: {
    fontFamily: 'Nunito, Arial, sans-serif, dinpro-light',
    fontWeight: 300,
  },
  secondary: {
    color: secondaryText,
  },
  accent: {
    color: accentText,
  },
  label: {
    color: labelText,
  },
  white: {
    color: buttonText,
  },
  error: {
    color: errorColor,
  },
  xxxxl: {
    fontSize: xxxxlStyles.size,
    lineHeight: xxxxlStyles.lineHeight,
    letterSpacing: xxxxlStyles.spacing,
  },
  xxxl: {
    fontSize: xxxlStyles.size,
    lineHeight: xxxlStyles.lineHeight,
    letterSpacing: xxxlStyles.spacing,
  },
  xxl: {
    fontSize: xxlStyles.size,
    lineHeight: xxlStyles.lineHeight,
    letterSpacing: xxlStyles.spacing,
  },
  xl: {
    fontSize: xlStyles.size,
    lineHeight: xlStyles.lineHeight,
    letterSpacing: xlStyles.spacing,
  },
  l: {
    fontSize: lStyles.sizeMobile,
    lineHeight: lStyles.lineHeightMobile,
    letterSpacing: lStyles.spacing,
    [theme.breakpoints.up('sm')]: {
      fontSize: lStyles.size,
      lineHeight: lStyles.lineHeight,
    },
  },
  m: {
    fontSize: mStyles.size,
    lineHeight: mStyles.lineHeight,
    letterSpacing: mStyles.spacing,
  },
  s: {
    fontSize: sStyles.sizeMobile,
    lineHeight: sStyles.lineHeightMobile,
    letterSpacing: sStyles.spacing,
    [theme.breakpoints.up('sm')]: {
      fontSize: sStyles.size,
      lineHeight: sStyles.lineHeight,
    },
  },
  xs: {
    fontSize: xsStyles.size,
    lineHeight: xsStyles.lineHeight,
    letterSpacing: xsStyles.spacing,
  },
  xxs: {
    fontSize: xxsStyles.size,
    lineHeight: xxsStyles.lineHeight,
    letterSpacing: xxsStyles.spacing,
  },
});

/**
 * Text component.
 */
class T extends PureComponent {
  getSizeClass = () => {
    const {
      classes: classesProp,
      xxxxl,
      xxxl,
      xxl,
      xl,
      l,
      m,
      s,
      xs,
      xxs,
      test,
    } = this.props;
    const classes = test ? reduceClasses(classesProp) : classesProp;

    if (xxxxl) return classes.xxxxl;
    if (xxxl) return classes.xxxl;
    if (xxl) return classes.xxl;
    if (xl) return classes.xl;
    if (l) return classes.l;
    if (m) return classes.m;
    if (s) return classes.s;
    if (xs) return classes.xs;
    if (xxs) return classes.xxs;
    return '';
  };

  getFamilyClass = () => {
    const {
      classes: classesProp,
      black,
      bold,
      medium,
      light,
      test,
    } = this.props;
    const classes = test ? reduceClasses(classesProp) : classesProp;

    if (black) return classes.black;
    if (bold) return classes.bold;
    if (medium) return classes.medium;
    if (light) return classes.light;
    return '';
  };

  render() {
    const {
      classes: classesProp,
      className: classNameProp,
      clickable,
      secondary,
      accent,
      label,
      white,
      error,
      children,
      color,
      size,
      lineHeight,
      dev,
      test,
      xxxxl,
      xxxl,
      xxl,
      xl,
      l,
      m,
      s,
      xs,
      xxs, // eslint-disable-line no-unused-vars
      black,
      bold,
      medium,
      light, // eslint-disable-line no-unused-vars
      ...other
    } = this.props;
    const classes = test ? reduceClasses(classesProp) : classesProp;

    if (dev) console.log('rendering text component');

    return (
      <span
        className={classNames(
          {
            [classes.clickable]: clickable,
            [classes.secondary]: secondary,
            [classes.accent]: accent,
            [classes.label]: label,
            [classes.white]: white,
            [classes.error]: error,
          },
          classes.root,
          this.getSizeClass(),
          this.getFamilyClass(),
          classNameProp,
        )}
        style={{
          color,
          fontSize: size ? `${size}px` : '',
          lineHeight: lineHeight ? `${lineHeight}px` : '',
        }}
        {...other}
      >
        {children}
      </span>
    );
  }
}

T.propTypes = {
  /** Classes from the styleSheet generated. */
  classes: shape({}).isRequired,
  /** Useful to extend the styleSheet applied to the text. */
  className: string,
  /** Pointer cursor. */
  clickable: bool,
  /** The Largest text. */
  xxxxl: bool,
  /** Almost the largest text. */
  xxxl: bool,
  /** Not the Largest text. */
  xxl: bool,
  /** Extra large text. */
  xl: bool,
  /** Large text. */
  l: bool,
  /** Medium text. */
  m: bool,
  /** Small text. */
  s: bool,
  /** Extra small text. */
  xs: bool,
  /** Smallest text. */
  xxs: bool,
  /** Set dinpro-black as font-family. */
  black: bool,
  /** Set dinpro-bold as font-family. */
  bold: bool,
  /** Set dinpro-medium as font-family. */
  medium: bool,
  /** Set dinpro-light as font-family. */
  light: bool,
  /** Secondary text color. */
  secondary: bool,
  /** Accent text color. */
  accent: bool,
  /** Default label text color. */
  label: bool,
  /** Default text color for buttons. */
  white: bool,
  /** Error text color. */
  error: bool,
  /** Text to be shown. */
  children: node.isRequired,
  /** Custom text color [ONLY to match specific designs]. */
  color: string,
  /** Custom text size [ONLY to match specific designs]. */
  size: number,
  /** Custom line height [ONLY to match specific designs]. */
  lineHeight: number,
  /** Useful for debug */
  dev: bool,
  /** Useful for snapshot testing. */
  test: bool,
};

T.defaultProps = {
  className: undefined,
  clickable: false,
  xxxxl: false,
  xxxl: false,
  xxl: false,
  xl: false,
  l: false,
  m: false,
  s: false,
  xs: false,
  xxs: false,
  black: false,
  bold: false,
  medium: false,
  light: false,
  secondary: false,
  accent: false,
  label: false,
  white: false,
  error: false,
  color: undefined,
  size: undefined,
  lineHeight: undefined,
  dev: false,
  test: false,
};

export default withStyles(style, { name: 'Text' })(T);
